import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppDispatch } from 'flow/hooks/useRedux';
import moment from 'moment';

import { setSnackbar } from 'flow/actions/app-actions';
import OtherPerson from 'flow/components/CallHistory/OtherPerson';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { CallRecord } from 'flow/types/CallRecord';
import { Transcription } from 'flow/types/Transcription';
import { TypedMap } from 'flow/types/TypedMap';
import { getDateOfPersonalCallRecord } from 'flow/utils/call-records-utils';
import { useTranscribeRecordedCallWithProgress } from 'flow/hooks/api/useTranscribeRecordedCallWithProgress';

interface Props {
    callRecord?: TypedMap<CallRecord>;
}

const TranscriptionDetailModal = ({ callRecord }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();

    const [showTranscription, setShowTranscription] =
        useState<keyof Transcription>('fullTranscript');

    const { data: transcription, refreshTranscription } =
        useTranscribeRecordedCallWithProgress(
            callRecord?.getIn(['recordedCall', 'key']),
            callRecord?.getIn(['recordedCall', 'isPersonalRecording']),
        );

    const onClickCopy = () => {
        navigator.clipboard
            .writeText(transcription?.[showTranscription] || '')
            .then(() => {
                dispatch(
                    setSnackbar({
                        message: i18next.t('snackbar.copied_to_clipboard'),
                    }),
                );
            });
    };

    return (
        <BaseModal
            open={true}
            header={
                showTranscription === 'fullTranscript'
                    ? i18next.t('ai.transcript.transcript')
                    : i18next.t('ai.summary.summary')
            }
            onSubmit={() => {}}
            confirmButtonText={i18next.t('Tooltip.Dialog.Close')}
            hideCancelButton={true}
            className="w-[600px]"
        >
            <div className="flex flex-col gap-3">
                <span className="mb-1 text-sm/4 text-gray-500">
                    <Trans
                        i18n={i18next}
                        i18nKey={'call.modal.description'}
                        components={[
                            <Person
                                key="caller"
                                callRecord={callRecord}
                                person="caller"
                            />,
                            <Person
                                key="callee"
                                callRecord={callRecord}
                                person="callee"
                            />,
                        ]}
                        values={{
                            timestamp: moment(
                                getDateOfPersonalCallRecord(callRecord),
                            ).format('MMMM D, YYYY HH:mm'),
                        }}
                    />
                </span>
                <span className="whitespace-pre-line">
                    {transcription[showTranscription]}
                </span>
                <div className="flex gap-3">
                    {showTranscription === 'fullTranscript' ? (
                        <Button
                            onClick={() => setShowTranscription('summary')}
                            color="gray"
                            className="!min-w-0"
                            iconKey="summarize"
                            size="small"
                        >
                            {i18next.t('ai.summary.summary')}
                        </Button>
                    ) : (
                        <Button
                            onClick={() =>
                                setShowTranscription('fullTranscript')
                            }
                            color="gray"
                            className="!min-w-0"
                            iconKey="text_snippet"
                            size="small"
                        >
                            {i18next.t('ai.transcript.transcript')}
                        </Button>
                    )}

                    <Button
                        onClick={() => {
                            refreshTranscription();
                            closeModal();
                        }}
                        color="gray"
                        className="!min-w-0"
                        iconKey="replay"
                        size="small"
                    >
                        {i18next.t('ai.transcript.regenerate')}
                    </Button>

                    <Button
                        onClick={onClickCopy}
                        color="gray"
                        className="!min-w-0"
                        iconKey="copy"
                        size="small"
                    >
                        {i18next.t('Tooltip.Title.Copy')}
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
};

interface PersonProps {
    callRecord: TypedMap<CallRecord>;
    person: 'callee' | 'caller';
}

const Person = ({ callRecord, person }: PersonProps) => {
    const { i18next } = useAppContext();

    const isYou =
        (person === 'caller' && callRecord.get('direction') === 'outgoing') ||
        (person === 'callee' && callRecord.get('direction') === 'incoming');

    return isYou ? (
        i18next.t('chat.you')
    ) : (
        <OtherPerson callRecord={callRecord} />
    );
};

export default TranscriptionDetailModal;
